<template>
  <ContentCard class="mb-8">
    <template #header>
      <ContentCardSubheader>
        {{ t('Reviews.product.view.campaignsTitle', { productName: product.title }) }}
        <TooltipWrapper class="ml-2">
          <template #trigger>
            <a :href="tooltipLink" target="_blank"><CircleHelpIcon class="lucide-icon-s text-icon-subtlest" /></a>
          </template>
          <template #content>
            <div v-html="tooltipText"></div>
          </template>
        </TooltipWrapper>
        <template #actions>
          <RouterLink
            class="btn-primary-color-s"
            data-testid="reviews-new-campaign-button"
            :to="{ name: ReviewsRouteName.ReviewsProductCampaignNew, params: { productId: product.id } }"
            @click="track('campaign_create_start', { surface: 'editor', entity_type: 'tool', entity_id: product.id })"
          >
            {{ t('Reviews.product.action.newCampaign') }} <PlusIcon class="lucide-icon-s" />
          </RouterLink>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="flex gap-4 pt-4 pb-8">
      <BudgetBox
        v-if="product.currentBudget"
        class="w-1/3 shrink-0"
        :end-date="new Date(product.currentBudget.endDate)"
        :used-budget="product.currentBudget.usedValue || 0"
        :total-budget="product.currentBudget.value + (product.currentBudget.additionalValue || 0)"
      />
      <GlobalBudgetSkeleton v-else class="w-1/3 shrink-0" :entity-id="product.id" />
      <TopRatedBadgeBox
        v-if="!loadingProgress && topRatedProgress"
        class="!w-1/3 shrink-0"
        :timeframe="currentQuarter"
        :review-count="topRatedProgress.reviewCount"
        :minimum-review-count="topRatedProgress.minimumReviewCount"
        :average-score="topRatedProgress.averageScore ?? null"
        :eligible="topRatedProgress.eligible"
      />
      <div
        v-else-if="loadingProgress"
        data-testid="top-rated-badge-loading"
        class="flex w-1/3 shrink-0 items-center justify-center rounded-sm bg-surface-elevation-1 shadow-sm"
      >
        <CircleSpinner />
      </div>
      <div class="relative w-1/3 pl-6">
        <GenericInvitationLinks subject="product" :slug="product.slug" cta-position="product_campaigns" orientation="column" />
        <ToggleSwitch v-model="showInactive" class="absolute bottom-0" size="s" data-testid="reviews-campaigns-inactive">
          {{ t('Reviews.product.campaign.showInactive') }}
        </ToggleSwitch>
      </div>
    </div>
    <CampaignsTable
      class="pb-6"
      :product-id="product.id"
      :campaigns="result?.manage?.incentivizationCampaigns || []"
      :remaining-global-budget="product.currentBudget?.remainingValue || 0"
      :loading="loading"
    />
  </ContentCard>
</template>

<script setup lang="ts">
import { ToggleSwitch, TooltipWrapper, CircleSpinner } from '@ramp106/omrjs-core-ui'
import { PlusIcon, CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  IncentivizationCampaignStatusEnum,
  type ProductData,
  useReviewsIncentivizationCampaignsQuery,
  useReviewsTopRatedBadgeProgressQuery,
} from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types.ts'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import BudgetBox from './BudgetBox.vue'
import CampaignsTable from './CampaignsTable.vue'
import GenericInvitationLinks from './GenericInvitationLinks.vue'
import GlobalBudgetSkeleton from './GlobalBudgetSkeleton.vue'
import TopRatedBadgeBox from './TopRatedBadgeBox.vue'

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const showInactive = ref(false)

watch(showInactive, (value) => {
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'manage_tool_campaigns',
    filter_type: 'tickbox',
    filter_name: 'show_inactive',
    filter_value: String(value),
  })
})

const tooltipText = t('Reviews.product.view.campaigns.campaignHelp')
const tooltipLink = t('Reviews.product.view.campaigns.campaignHelpLink')

const variables = computed(() => ({
  productId: props.product.id,
  statuses: showInactive.value
    ? [
        IncentivizationCampaignStatusEnum.Active,
        IncentivizationCampaignStatusEnum.Pending,
        IncentivizationCampaignStatusEnum.Disabled,
        IncentivizationCampaignStatusEnum.Inactive,
      ]
    : [IncentivizationCampaignStatusEnum.Active, IncentivizationCampaignStatusEnum.Disabled, IncentivizationCampaignStatusEnum.Pending],
}))

const { loading, result } = useReviewsIncentivizationCampaignsQuery(variables, { clientId: 'reviews', fetchPolicy: 'cache-and-network' })

const currentDate = new Date()
const currentYear = currentDate.getFullYear()
const currentMonth = currentDate.getMonth()
let currentQuarter
if (currentMonth < 3) {
  currentQuarter = `Q1 / ${currentYear}`
} else if (currentMonth < 6) {
  currentQuarter = `Q2 / ${currentYear}`
} else if (currentMonth < 9) {
  currentQuarter = `Q3 / ${currentYear}`
} else {
  currentQuarter = `Q4 / ${currentYear}`
}

const { loading: loadingProgress, result: progressResult } = useReviewsTopRatedBadgeProgressQuery(
  { id: props.product.id },
  { clientId: 'reviews' },
)
const topRatedProgress = computed(() => progressResult.value?.manage?.topRatedBadgeProgress)
</script>
